import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function QuestionForm({ sectionId, onQuestionCreated }) {
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'text',
    required: false,
    scoring_weight: 1.0,
    control_required: false,
    options: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [optionText, setOptionText] = useState('');
  const [optionScore, setOptionScore] = useState(1);

  const questionTypes = [
    { value: 'text', label: 'Text Response' },
    { value: 'select', label: 'Single Select' },
    { value: 'multi_select', label: 'Multiple Select' },
    { value: 'scale', label: 'Risk Scale (1-5)' },
    { value: 'control_rating', label: 'Control Rating (0-4)' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'scoring_weight' ? parseFloat(value) || 1.0 : 
              value
    }));
    setError('');
  };

  const addOption = () => {
    if (!optionText.trim()) return;
    
    const newOption = {
      value: optionText.toLowerCase().replace(/\s+/g, '_'),
      label: optionText.trim(),
      score: optionScore
    };
    
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, newOption]
    }));
    
    setOptionText('');
    setOptionScore(1);
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question_text.trim()) {
      setError('Question text is required');
      return;
    }

    if (['select', 'multi_select'].includes(formData.question_type) && formData.options.length === 0) {
      setError('At least one option is required for select questions');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        section_id: sectionId,
        ...formData,
        options: ['select', 'multi_select'].includes(formData.question_type) ? formData.options : null
      };

      const res = await fetch(`${API_BASE}/risk-assessment/questions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await res.json();
      
      if (data.success) {
        onQuestionCreated(data.question);
        setFormData({
          question_text: '',
          question_type: 'text',
          required: false,
          scoring_weight: 1.0,
          control_required: false,
          options: []
        });
      } else {
        setError(data.message || 'Failed to create question');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error(err);
    }
    setIsSubmitting(false);
  };

  const needsOptions = ['select', 'multi_select'].includes(formData.question_type);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Create New Question</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="question_text" className="block text-sm font-medium text-slate-700 mb-2">
            Question Text *
          </label>
          <textarea
            id="question_text"
            name="question_text"
            value={formData.question_text}
            onChange={handleChange}
            placeholder="Enter your assessment question..."
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            disabled={isSubmitting}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="question_type" className="block text-sm font-medium text-slate-700 mb-2">
              Question Type
            </label>
            <select
              id="question_type"
              name="question_type"
              value={formData.question_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={isSubmitting}
            >
              {questionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="scoring_weight" className="block text-sm font-medium text-slate-700 mb-2">
              Scoring Weight
            </label>
            <input
              type="number"
              id="scoring_weight"
              name="scoring_weight"
              value={formData.scoring_weight}
              onChange={handleChange}
              min="0.1"
              max="5.0"
              step="0.1"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="required"
              checked={formData.required}
              onChange={handleChange}
              className="rounded border-slate-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              disabled={isSubmitting}
            />
            <span className="ml-2 text-sm text-slate-700">Required Question</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              name="control_required"
              checked={formData.control_required}
              onChange={handleChange}
              className="rounded border-slate-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              disabled={isSubmitting}
            />
            <span className="ml-2 text-sm text-slate-700">Control Rating Required</span>
          </label>
        </div>

        {needsOptions && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Answer Options
            </label>
            
            {/* Add Option Form */}
            <div className="flex gap-2">
              <input
                type="text"
                value={optionText}
                onChange={(e) => setOptionText(e.target.value)}
                placeholder="Option text"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={isSubmitting}
              />
              <input
                type="number"
                value={optionScore}
                onChange={(e) => setOptionScore(parseInt(e.target.value) || 1)}
                placeholder="Score"
                min="1"
                max="5"
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={addOption}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                disabled={isSubmitting || !optionText.trim()}
              >
                Add
              </button>
            </div>

            {/* Options List */}
            {formData.options.length > 0 && (
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">{option.label}</span>
                      <span className="ml-2 text-sm text-slate-500">(Score: {option.score})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      disabled={isSubmitting}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !formData.question_text.trim()}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating...
            </div>
          ) : (
            'Create Question'
          )}
        </button>
      </form>
    </div>
  );
}
